import React, { useState } from 'react';
import Select, { components, Props, LoadingIndicatorProps, DropdownIndicatorProps } from 'react-select-5';
import { SpinnerIcon } from '@/components/common/Utils';
import Icon from '@/components/ui/Icon';

const { DropdownIndicator } = components || {};
const CustomDropdownIndicator = (props: DropdownIndicatorProps) => {
  const { selectProps: { menuIsOpen } } = props;
  const iconDirection = menuIsOpen ? 'CaretOpened' : 'CaretClosed';

  return (
    <DropdownIndicator {...props}>
      {<Icon icon={iconDirection} width={10} />}
    </DropdownIndicator>
  );
};

interface CustomLoadingIndicatorProps extends LoadingIndicatorProps {
  isLoading: boolean;
}

const CustomLoadingIndicator = ({ innerProps, isLoading }: CustomLoadingIndicatorProps) => {
  if (isLoading) return null;

  return <div {...innerProps}><SpinnerIcon /></div>;
};

const backgroundColor = ({ isSelected, isFocused }) => {
  if (isSelected) return '#f0f6ff';
  if (isFocused) return '#f8faff';

  return '#fff';
};

const styleOverrides = ({
  borderColorOverride = null,
  compactPadding = false,
  dropdownHeightOverride = null,
  isKeyboardNav = false,
  menuHeightOverride = null,
  fontSizeOverride = null,
  showCaret = false,
}) => ({
  control: (styles, { menuIsOpen, isFocused }) => {
    const borderColorStyle = {
      borderColor: borderColorOverride ?? 'hsl(0, 0%, 85%)',
      ...(menuIsOpen && { borderBottomColor: 'transparent' }),
    };

    return {
      ...styles,
      ...borderColorStyle,
      ...(!showCaret && { caretColor: 'transparent' }),
      borderBottomLeftRadius: menuIsOpen ? 0 : '4px',
      borderBottomRightRadius: menuIsOpen ? 0 : '4px',
      boxShadow: 0,
      outline: 0,
      position: 'relative',
      ...(menuHeightOverride && { height: menuHeightOverride }),
      ...(menuHeightOverride && { minHeight: menuHeightOverride }),
      ...(fontSizeOverride && { fontSize: fontSizeOverride }),
      ...(isFocused && !menuIsOpen && isKeyboardNav && {
        borderRadius: '4px',
        boxShadow: '0 0 0 2px white, 0 0 4px 4px #9747ff',
        outline: '2.5px solid #522e91',
        outlineOffset: '2px',
      }),
      '&:hover': {
        ...borderColorStyle,
      },
    };
  },
  menuPortal: styles => ({
    ...styles,
    zIndex: 1500,
  }),
  valueContainer: styles => ({
    ...styles,
    ...(compactPadding && { padding: '0 8px' }),
  }),
  dropdownIndicator: styles => ({
    ...styles,
    padding: compactPadding ? '0 4px' : '8px 10px',
    color: 'hsl(0, 0%, 40%)',
  }),
  indicatorSeparator: styles => ({
    ...styles,
    display: 'none',
  }),
  menu: styles => ({
    ...styles,
    zIndex: 1500,
    marginTop: 0,
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
  }),
  menuList: styles => ({
    ...styles,
    padding: 0,
    borderBottomRightRadius: '4px',
    borderBottomLeftRadius: '4px',
    ...(dropdownHeightOverride && { height: dropdownHeightOverride }),
  }),
  option: (styles, { isFocused, isSelected, isDisabled }) => ({
    ...styles,
    backgroundColor: backgroundColor({ isSelected, isFocused }),
    color: isDisabled ? '#ccc' : '#333',
    cursor: isDisabled ? 'default' : 'pointer',
    ...(fontSizeOverride && { fontSize: fontSizeOverride }),
  }),
  input: styles => ({
    ...styles,
    ...(compactPadding && { margin: 0, padding: 0 }),
  }),
  singleValue: styles => ({
    ...styles,
    padding: compactPadding ? '0' : '4px 0',
  }),
});

export interface ReactSelect5Props extends Props {
  borderColorOverride?: string;
  compactPadding?: boolean;
  showCaret?: boolean;
  dropdownHeightOverride?: number | string;
  fontSizeOverride?: number | string;
  menuHeightOverride?: number | string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

// `className` can be used with the `classNames` prop of react-select-5 to apply custom styles
export interface Option {
  className?: string;
  isDisabled?: boolean;
  label: string;
  value: string;
}

const ReactSelect5 = React.forwardRef((props: ReactSelect5Props, ref) => {
  const [isKeyboardNav, setIsKeyboardNav] = useState(true);

  const {
    borderColorOverride,
    compactPadding,
    showCaret,
    dropdownHeightOverride,
    fontSizeOverride,
    menuHeightOverride,
    onClick,
    ...selectProps
  } = props;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    setIsKeyboardNav(true);
    selectProps.onKeyDown?.(e);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsKeyboardNav(false);
    onClick?.(e);
  };

  return (
    <div role="presentation" onClick={handleClick}>
      <Select
        // @ts-ignore
        ref={ref}
        // Shrink the menu to the available space at short viewports (flipping up
        // when there's more room above) instead of react-select's default
        // page-scroll fallback, which clips the menu once the space drops below
        // minMenuHeight (WCAG 1.4.10). menuPosition="fixed" makes the space math
        // viewport-relative — the default measures against the nearest scroll
        // parent, which overshoots the screen at reflow widths.
        menuPlacement="auto"
        // Portaling to <body> keeps the fixed-position menu out of transformed
        // ancestors (modals center via translate) and clamped stacking contexts.
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        menuPosition="fixed"
        menuShouldScrollIntoView={false}
        minMenuHeight={80}
        {...selectProps}
        classNamePrefix="react-select"
        components={{
          DropdownIndicator: CustomDropdownIndicator,
          LoadingIndicator: CustomLoadingIndicator,
        }}
        styles={styleOverrides({
          borderColorOverride,
          compactPadding,
          dropdownHeightOverride,
          fontSizeOverride,
          isKeyboardNav,
          menuHeightOverride,
          showCaret,
        })}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
});

export default ReactSelect5;
